import { z } from 'zod';

export const UserRoleSchema = z.enum(['admin', 'editor', 'author']);
export type UserRole = z.infer<typeof UserRoleSchema>;

export const LoginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});

export const InviteUserSchema = z.object({
  email: z.string().email(),
  name: z.string().min(1).max(200),
  role: UserRoleSchema,
});

export const UpdateUserSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  bio: z.string().max(500).optional().nullable(),
  avatarAssetId: z.string().optional().nullable(),
});

export const ChangePasswordSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: z.string().min(8).max(100),
});
export type ChangePasswordInput = z.infer<typeof ChangePasswordSchema>;

export const UserSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  name: z.string(),
  role: UserRoleSchema,
  bio: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  isActive: z.boolean(),
  /**
   * System administrator for this deployment (private-beta operator): may
   * create accounts and assign workspace plans. Returned by
   * POST /api/auth/login and GET /api/auth/me so the editor can show the
   * admin area — the server re-checks it on every admin request.
   */
  isPlatformAdmin: z.boolean(),
  lastLoginAt: z.number().nullable(),
  createdAt: z.number(),
});

export type User = z.infer<typeof UserSchema>;
export type LoginInput = z.infer<typeof LoginSchema>;
export type InviteUserInput = z.infer<typeof InviteUserSchema>;
export type UpdateUserInput = z.infer<typeof UpdateUserSchema>;

/**
 * The signed-in user as the API actually returns it (POST /api/auth/login,
 * GET /api/auth/me, POST /api/setup) and as the editor keeps it in the auth
 * store. `UserSchema` above is the fuller account record; this is the session
 * projection — the two are separate because the session never carries
 * `avatarUrl`/`lastLoginAt`/`createdAt`, and code that assumed it did was
 * typing a lie.
 */
export const SessionUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  role: UserRoleSchema,
  bio: z.string().nullable().optional(),
  /** System administrator for this deployment — see packages/shared/src/admin.ts. */
  isPlatformAdmin: z.boolean(),
});
export type SessionUser = z.infer<typeof SessionUserSchema>;
